import Service, { service } from '@ember/service';
import { getSingleValueForTasField } from '../utils/tuition-assistance/fields.ts';
import { task, timeout } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { date, minValue, maxValue, object, pipe } from 'valibot';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type FeaturesService from './features.ts';
import type IntlService from 'ember-intl/services/intl';

// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import type SessionContextService from './session-context.ts';
import type { TasApplicationExtension as TASApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasEligibilityExtension as TasEligibilityModel } from 'tio-data/data/resources/tas-eligibility.ext';
import type { TasParticipantExtension as TasParticipantModel } from 'tio-data/data/resources/tas-participant.ext';
import type { TasProgramTemplateExtension as TASProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import type TuitionAssistanceLimitsSummaryComponent from '../components/tuition-assistance/limits-summary.gts';
import { query } from '@warp-drive/utilities/json-api';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { buildTioAuthHeaders } from 'tio-data/utils/auth-headers';
import {
  buildTasTransitionFieldData,
  requestEvidenceApprovalRequest,
} from 'tio-data/requests/tas-application-actions';
import { buildTasActorFromSession } from 'tio-data/utils/tas-actor';
import { reloadRecord } from 'tio-data/requests/save-record';

type LimitsRequestParamsSignature = {
  employee_ids: string[];
  excluded_item_ids: string[];
  company_id: string;
  product: string;
  scheme?: string;
  batch?: string;
  group?: string | null;
  date?: string;
  date_based_on?: string;
  course_start?: string;
  course_end?: string;
  end_of_year_cutoff?: number | null;
  requester: string | null;
  approval?: string | null;
  payment_date?: string;
  dependent_id?: string;
  application_id?: string;
  is_prepaid?: boolean;
};

type LimitsStatusRequestResponseSignature = {
  data: LimitsStatusDataSignature[];
};

type LimitName =
  | 'company_annual'
  | 'company'
  | 'product_annual'
  | 'product'
  | 'batch_annual'
  | 'batch'
  | 'scheme_annual'
  | 'scheme'
  | 'none';

export type LimitCapacity = 'TAS.LimitCapacity.MONETARY' | 'TAS.LimitCapacity.CREDITS';
export type LimitProduct = 'TA' | 'SLR' | 'DEPENDENT_TA';

export type LimitsStatusDataSignature = {
  annual_end_date: string;
  annual_start_date: string;
  as_of_date?: string;
  available: number | null;
  available_with_override: number | null;
  details: {
    company: LimitsDetailSignature;
    product: LimitsDetailSignature;
    batch: LimitsDetailSignature;
    scheme: LimitsDetailSignature;
  };
  employee_id: string;
  hard_limit: LimitName;
  limit_capacity?: LimitCapacity;
  product: string | null;
  scheme: string | null;
  soft_limit: LimitName;
  external_annual_usage?: number;
};

export type LimitsDetailSignature = {
  annual_limit: number | null;
  annual_usage: number;
  available: number | null;
  balance_multiplier?: number;
  hard: boolean | null;
  limit: number | null;
  one_month_limit: number | null;
  one_month_usage: number;
  six_month_limit: number | null;
  six_month_usage: number;
  taxable_limit: number | null;
  three_month_limit: number | null;
  three_month_usage: number;
  usage: number;
  external_annual_usage?: number;
};

export type EligibilityStatusSignature = {
  employee_id: string;
  application_id: string;
  lock_basis: string;
  lock_date: string | null;
  participant_state: string | null;
  employment_type: string | null;
  deactivated_on: string | null;
  pay_deactivated_employees: boolean;
  ignore_policies: boolean;
  // Program eligibility period(s) [start, end] the lock date is checked against.
  // null when the participant has no eligibility record for the program.
  // Optional: responses from before tuitionio/tio-api#9506 omit this field.
  eligibility_periods?: Array<[string, string]> | null;
  // Whether the lock date falls inside one of `eligibility_periods`.
  // null when the check does not apply to the lock basis (submission-based locks).
  // Optional: responses from before tuitionio/tio-api#9506 omit this field.
  within_eligibility_period?: boolean | null;
  is_eligible: boolean;
};

type ApplicationValidationResponseSignature = {
  can_submit: boolean;
  reduction_needed: number;
};

type CourseDateValidationResult = {
  date: Date;
  message: string;
};

export default class TuitionAssistanceService extends Service {
  @service declare intl: IntlService;
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare features: FeaturesService;

  activeLimitsSummaryComponents: Set<TuitionAssistanceLimitsSummaryComponent> = new Set();

  @tracked activeEligibilities: TasEligibilityModel[] = [];
  @tracked limitsData: LimitsStatusDataSignature | undefined = undefined;
  @tracked gpaBasedAmount: number = 0;
  @tracked programTemplate: TASProgramTemplateModel | undefined = undefined;

  // WarpDrive migration Phase 2.4: buildBaseURL reads the globally-configured
  // host (set via setBuildURLConfig at app boot) — no adapter needed.
  buildUrl(resourcePath: string, subPath?: string): string {
    const opts: QueryUrlOptions = {
      identifier: { type: resourcePath },
      op: 'query',
      resourcePath,
    };
    const base = buildBaseURL(opts);
    return subPath ? `${base}/${subPath.replace(/^\/+/, '')}` : base;
  }

  // WarpDrive migration Phase 2.4: buildTioAuthHeaders is owner-free —
  // it reads authContext at call time, no adapter required.
  get tioAuthHeaders(): Headers {
    return buildTioAuthHeaders({
      apiKey: this.features.ENV?.apiKey ?? '',
      contentType: 'application/json',
      accept: 'application/json',
    });
  }

  get availableVoucherAmount() {
    return this.limitsData?.available ?? 0;
  }

  getApplicationLimitStatusByEmployeeId = async (
    employee: EmployeeModel
  ): Promise<LimitsStatusDataSignature | undefined> => {
    // WarpDrive migration Phase 2.4: replace adapter.ajax with direct fetch;
    // URL built via buildBaseURL (reads globally-configured host), auth headers
    // via buildTioAuthHeaders (owner-free, reads authContext at call time).
    const baseUrl = this.buildUrl('limits/status');

    const company = employee.company;
    const employeeId = employee.id;
    const companyId = company?.id;

    if (!employeeId || !companyId) {
      console.error(
        'Error in `getApplicationLimitStatusByEmployeeId`: Employee or company id is missing.',
        {
          employeeId,
          companyId,
        }
      );
      return undefined;
    }

    const params: LimitsRequestParamsSignature = {
      employee_ids: [employeeId],
      excluded_item_ids: [],
      company_id: companyId,
      requester: 'employee',
      product: 'TA',
    };

    // Serialize GET params the same way adapter.ajax({ data }) did.
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (Array.isArray(v)) {
        for (const item of v) {
          qs.append(`${k}[]`, String(item));
        }
      } else if (v !== undefined && v !== null) {
        qs.set(k, String(v));
      }
    }

    try {
      const response = await fetch(`${baseUrl}?${qs.toString()}`, {
        headers: this.tioAuthHeaders,
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch limits status: ${response.status}`);
      }

      const limitsStatus: LimitsStatusRequestResponseSignature = await response.json();
      const limitsData: LimitsStatusDataSignature | undefined = limitsStatus?.data?.[0];
      return limitsData;
    } catch (error) {
      console.error('Error fetching limits status:', error);
      throw error;
    }
  };

  /**
   * Simplified V2 limits endpoint that only requires employee ID and optional application ID.
   * This replaces the legacy complex parameter-heavy approach with backend-driven logic.
   * Uses the /api/v2/limits/status endpoint which handles all complex logic server-side.
   *
   * @param employee - The employee to get limits for
   * @param application - Optional application for context-specific limits
   * @param paidOnly - Optional flag to filter only paid applications (defaults to undefined)
   * @returns Promise resolving to limits status data
   */
  getV2LimitsStatus = async (
    employee: EmployeeModel,
    application?: TASApplicationModel,
    date?: string,
    programInstanceId?: string,
    product: LimitProduct = 'TA',
    planId?: string
  ): Promise<LimitsStatusDataSignature | undefined> => {
    // WarpDrive migration Phase 2.4: buildBaseURL replaces this.host (adapter).
    const url = this.buildUrl('api/v2/limits/status');

    if (!employee.id) {
      console.error('Error fetching simplified limits status: employee id is missing.', {
        employeeId: employee.id,
        applicationId: application?.id,
      });
      return undefined;
    }

    const params = new URLSearchParams();
    params.append('employee_ids[]', employee.id);
    params.append('product', product);

    if (application?.id) {
      params.append('application_id', application.id);
    }

    if (programInstanceId) {
      params.append('program_instance_id', programInstanceId);
    }

    if (planId) {
      params.append('plan_id', planId);
    }

    params.append('paid_only', 'false');

    if (date) {
      params.append('date', date);
    }

    try {
      const response = await fetch(`${url}?${params.toString()}`, {
        // WarpDrive migration Phase 2.4: buildTioAuthHeaders replaces this.headers (adapter).
        headers: this.tioAuthHeaders,
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch limits status: ${response.status}`);
      }

      const parsed: LimitsStatusRequestResponseSignature = await response.json();

      const limitsData = parsed?.data?.[0];
      if (limitsData) {
        this.limitsData = limitsData;
      }

      return limitsData;
    } catch (error) {
      console.error('Error fetching simplified limits status:', {
        employeeId: employee.id,
        applicationId: application?.id,
        error,
      });
      throw error;
    }
  };

  getV2SlrLimitsStatus = async (
    employee: EmployeeModel,
    planId?: string
  ): Promise<LimitsStatusDataSignature | undefined> => {
    return this.getV2LimitsStatus(employee, undefined, undefined, undefined, 'SLR', planId);
  };

  /**
   * Fetches eligibility status for a TAS application from the V2 endpoint.
   * Returns lock basis, historical participant state, deactivation status,
   * and computed eligibility boolean.
   *
   * @param applicationId - The TAS Application ID
   * @returns Promise resolving to eligibility status data
   */
  getEligibilityStatus = async (
    applicationId: string
  ): Promise<EligibilityStatusSignature | undefined> => {
    // WarpDrive migration Phase 2.4: buildBaseURL + buildTioAuthHeaders replace
    // this.host / this.headers (adapter).
    const url = this.buildUrl('api/v2/applications', `${applicationId}/eligibility-status`);

    try {
      const response = await fetch(url, {
        headers: this.tioAuthHeaders,
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch eligibility status: ${response.status}`);
      }

      const parsed: { data: EligibilityStatusSignature } = await response.json();
      return parsed?.data;
    } catch (error) {
      console.error('Error fetching eligibility status:', {
        applicationId,
        error,
      });
      throw error;
    }
  };

  validateAmountRequestedAgainstCurrentLimitsStatus = async (
    application: TASApplicationModel,
    employee: EmployeeModel
  ): Promise<ApplicationValidationResponseSignature> => {
    const allowSubmissionBeyondLimits =
      getSingleValueForTasField(
        'ALLOW_PRE_APPROVAL_SUBMISSION_BEYOND_LIMIT',
        application.tasProgramInstance.tasProgramTemplate.fields
      ) || false;
    if (allowSubmissionBeyondLimits) {
      return {
        can_submit: true,
        reduction_needed: 0,
      };
    }

    const limitStatus = await this.getV2LimitsStatus(employee, application);

    const remainingBenefit = limitStatus?.available ?? null;
    if (remainingBenefit === null) {
      return {
        can_submit: true,
        reduction_needed: 0,
      };
    }

    const isCreditBased = limitStatus?.limit_capacity === 'TAS.LimitCapacity.CREDITS';
    const amountRequested = isCreditBased
      ? application.totalCredits || 0
      : application.requestedTotal || 0;
    const remainder = remainingBenefit - amountRequested;
    const isValid = remainder >= 0;

    return {
      can_submit: isValid,
      reduction_needed: isValid ? 0 : Math.abs(remainder),
    };
  };

  triggerAutoEvidenceApprovalForApplication = async (
    application: TASApplicationModel,
    comment: string = ''
  ) => {
    if (!getSingleValueForTasField('WAIVE_EVIDENCE_APPROVAL_REQUIREMENT', application.fields)) {
      console.error(
        `Application ${application.id} cannot be submitted for auto approval without evidence approval waiver.`
      );
      return;
    }

    await this.autoApproveEvidenceTask.perform(application, comment);
  };

  /* At this point, the application should already have evidence approval waived by courses approval.
   * That means that as soon as we auto-request evidence approval, it should move to fulfilled as we expect.
   * This action lives on a service because of the distributed nature of the state machine. We need to ensure
   * this task is not cancelled because of a parent component being destroyed.
   * However, we do draw the line at 15 seconds of polling for the application
   * to move into valid state for requesting evidence approval.
   */
  autoApproveEvidenceTask = task(
    { maxConcurrency: 10, enqueue: true },
    async (application: TASApplicationModel, comment: string = '') => {
      let attemptCount = 0;

      while (application.state !== 'TAS.ApplicationState.ATTEND' && attemptCount < 30) {
        attemptCount++;
        await timeout(500);
        await reloadRecord(this.store, application);
      }

      try {
        // WarpDrive mutation builder (Phase 2.7) — replaces
        // store.adapterFor('tas-application').requestCourseEvidence(application, comment)
        // (triggerActionAndReload → request-evidence-approval transition + reload).
        await this.store.request(
          requestEvidenceApprovalRequest(
            application.id as string,
            buildTasActorFromSession(this.sessionContext),
            comment,
            buildTasTransitionFieldData(application.fields)
          )
        );
        await reloadRecord(this.store, application);
      } catch (e) {
        console.error('autoApproveEvidenceTask encountered an error', {
          applicationId: application.id,
          error: e,
        });
      }
    }
  );

  registerLimitsSummaryComponent = (component: TuitionAssistanceLimitsSummaryComponent) => {
    this.activeLimitsSummaryComponents.add(component);
  };

  unregisterLimitsSummaryComponent = (component: TuitionAssistanceLimitsSummaryComponent) => {
    this.activeLimitsSummaryComponents.delete(component);
  };

  refreshActiveLimitsSummaryComponents = () => {
    try {
      this.activeLimitsSummaryComponents.forEach((component) => component.refreshData());
    } catch (e) {
      console.error('Error refreshing active limits summary components', {
        error: e,
      });
    }
  };

  loadEligibilitiesForCurrentEmployee = async (participant?: TasParticipantModel) => {
    const currentEmployee = this.sessionContext.currentEmployee;
    if (!currentEmployee) {
      console.warn(
        '[tuition-assistance] loadEligibilitiesForCurrentEmployee called without a currentEmployee'
      );
      this.activeEligibilities = [];
      return;
    }
    try {
      let participantToUse = participant;

      // If no participant provided, query for it (fallback for backward compatibility)
      if (!participantToUse) {
        // WarpDrive builder read (Phase 2.4) — replaces store.query;
        // filter nested keys flattened to bracket notation as required by
        // the builder's QueryParamsSource type.
        const { content } = (await this.store.request(
          query<TasParticipantModel>('tas-participant', {
            'filter[employee]': currentEmployee.id,
            include: 'tas-eligibilities',
          })
        )) as { content: { data: TasParticipantModel[] } };
        const participants = content.data;

        if (!participants || participants.length === 0) {
          console.error('loadEligibilitiesForCurrentEmployee: No participants found', {
            employeeId: currentEmployee.id,
          });
          this.activeEligibilities = [];
          return;
        }

        participantToUse = participants[0];
      }

      const eligibilities = participantToUse?.tasEligibilities || [];

      if (eligibilities.length === 0) {
        console.warn(
          'loadEligibilitiesForCurrentEmployee: No eligibilities found for participant',
          {
            employeeId: currentEmployee.id,
            participantId: participantToUse?.id,
          }
        );
      }

      this.activeEligibilities = eligibilities.filter(
        (model: TasEligibilityModel) => model.isActive
      );
    } catch (error) {
      console.error('loadEligibilitiesForCurrentEmployee: Error loading eligibilities', {
        employeeId: currentEmployee.id,
        error,
      });
      this.activeEligibilities = [];
    }
  };

  hasActiveEligibilityForProgramTemplate = (programTemplate: TASProgramTemplateModel) => {
    // Defensive check: if activeEligibilities is empty, it might not have been loaded yet
    if (this.activeEligibilities.length === 0) {
      console.warn('hasActiveEligibilityForProgramTemplate: activeEligibilities is empty', {
        programTemplateId: programTemplate.id,
        programTemplateCode: programTemplate.code,
      });
    }

    return this.activeEligibilities.some(
      (eligibility: TasEligibilityModel) => eligibility.code === programTemplate.code
    );
  };

  waitingPeriodEndDate = (waitingPeriod: number, tasParticipant: TasParticipantModel) => {
    const waitingPeriodToNumber = Number(waitingPeriod);
    if (!tasParticipant?.employmentStartDate || waitingPeriodToNumber === 0) {
      return ''; // Return '' to indicate that the waiting period end date can't be calculated or is 0
    }

    const hireDate = new Date(tasParticipant.employmentStartDate);
    const endDate = new Date(hireDate);
    endDate.setDate(hireDate.getDate() + waitingPeriodToNumber);
    return endDate;
  };

  ineligibleBasedOnWaitingPeriod = (
    waitingPeriod: number,
    tasParticipant: TasParticipantModel,
    dateToCheck?: string
  ) => {
    const endDate = this.waitingPeriodEndDate(waitingPeriod, tasParticipant);
    if (!endDate) {
      return false; // If no end date, participant is not ineligible based on waiting period
    }

    // If no dateToCheck (typically from course start date) is provided, use today's date
    const applicationDate = dateToCheck ? new Date(dateToCheck) : new Date();
    const result = applicationDate < endDate;

    return result;
  };

  setTotalBasedOnGPA = (gpa: number) => {
    // values are in cents
    if (gpa >= 3.5) {
      this.gpaBasedAmount = 300000;
    } else if (gpa >= 2.75) {
      this.gpaBasedAmount = 225000;
    } else if (gpa >= 2.5) {
      this.gpaBasedAmount = 75000;
    } else {
      this.gpaBasedAmount = 0;
    }
    return this.gpaBasedAmount;
  };

  addDays(date: Date, days: number): Date {
    return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
  }

  firstDayToSubmitApplicationForPreApproval(
    programTemplate: TASProgramTemplateModel
  ): CourseDateValidationResult | null {
    const leadTimeDays = Number(programTemplate.coursesPreApprovalSubmissionMaxLeadTime) || null;
    if (!leadTimeDays) {
      return null;
    }

    const today = new Date();
    const submissionDeadline = this.addDays(today, leadTimeDays);
    const formattedDeadline = this.intl.formatDate(submissionDeadline, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    return {
      date: submissionDeadline,
      message: `Your employer requires that you submit your application within ${leadTimeDays} days of the start date. Change date to be on or before ${formattedDeadline}.`,
    };
  }

  courseMaximumBeginDateWithErrorMessaging(
    programTemplate: TASProgramTemplateModel
  ): CourseDateValidationResult | null {
    // If the program has a defined pre-approval submission lead time, no error messaging is needed.
    if (!programTemplate.coursesPreApprovalSubmissionMaxLeadTime) {
      return null;
    }
    const preApprovalMaxLeadTimeResult =
      this.firstDayToSubmitApplicationForPreApproval(programTemplate);
    if (!preApprovalMaxLeadTimeResult) {
      return {
        date: new Date('2099-01-01'),
        message: this.intl.t(
          'tuition_assistance.program_details.courses.validation.defaults.begin_date'
        ),
      };
    }

    return preApprovalMaxLeadTimeResult;
  }

  lastPossibleBeginDateWithErrorMessaging(
    programTemplate: TASProgramTemplateModel
  ): CourseDateValidationResult | null {
    let daysOffset: number | null = null;
    let policyMessage = '';

    const { lastDayToSubmitApplicationForPreApproval, coursesPreApprovalSubmissionMinLeadTime } =
      programTemplate;

    if (lastDayToSubmitApplicationForPreApproval) {
      daysOffset = -Number(lastDayToSubmitApplicationForPreApproval) || null;
      if (daysOffset) {
        policyMessage = `Your employer requires that you submit your application within ${Math.abs(daysOffset)} days after the start date.`;
      }
    } else if (coursesPreApprovalSubmissionMinLeadTime) {
      daysOffset = Number(coursesPreApprovalSubmissionMinLeadTime) || null;
      if (daysOffset) {
        policyMessage = `Your employer requires that you submit your application at a minimum of ${daysOffset} days before the start date.`;
      }
    }

    if (!daysOffset) {
      return null;
    }

    const today = new Date();
    const deadlineDate = this.addDays(today, daysOffset);

    return {
      date: deadlineDate,
      message: policyMessage,
    };
  }

  courseMinimumBeginDateWithErrorMessaging(
    programTemplate: TASProgramTemplateModel
  ): CourseDateValidationResult {
    const lastDayToSubmit = this.lastPossibleBeginDateWithErrorMessaging(programTemplate);
    if (!lastDayToSubmit) {
      return {
        date: new Date('2000-01-01'),
        message: this.intl.t(
          'tuition_assistance.program_details.courses.validation.defaults.begin_date'
        ),
      };
    }

    return lastDayToSubmit;
  }

  dynamicBeginDateValidationSchema = (
    programTemplate: TASProgramTemplateModel,
    endDate?: string
  ) => {
    const maxDate = this.courseMaximumBeginDateWithErrorMessaging(programTemplate);
    const minDate = this.courseMinimumBeginDateWithErrorMessaging(programTemplate);

    // If end date is provided and valid, start date must be on or before it
    const endDateConstraint =
      endDate && !isNaN(new Date(endDate).getTime())
        ? {
            date: new Date(endDate),
            message: this.intl.t(
              'tuition_assistance.program_details.courses.validation.start_before_end'
            ),
          }
        : null;

    // Use the earlier of maxDate or endDate as the upper bound
    const effectiveMaxDate =
      endDateConstraint && (!maxDate || endDateConstraint.date < maxDate.date)
        ? endDateConstraint
        : maxDate;

    const validators = [
      date(),
      ...(minDate ? [minValue<Date, Date, string>(minDate.date, minDate.message)] : []),
      ...(effectiveMaxDate
        ? [maxValue<Date, Date, string>(effectiveMaxDate.date, effectiveMaxDate.message)]
        : []),
    ] as const;

    const validation = object({
      dateEntered: pipe(...validators),
    });

    return validation;
  };
}
